/* eslint-disable max-lines-per-function */

import { useMemo, useState } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';
import { useTheme } from '@change-corgi/design-system-legacy/theme';

import { Highcharts } from 'src/app/shared/utils/highcharts';

import type { ChannelSignatureData } from '../../../../types';

type UseBarChartReturn = ModelHookResult<
	{
		options: Highcharts.Options;
		isShowingAllChannels: boolean;
		showButtons: boolean;
		highchartsModule: typeof Highcharts;
	},
	{ showAllChannels: () => void; showTopChannels: () => void }
>;

type ChartDataPoint = {
	name: string;
	className: string;
	y: number;
	color: string;
	custom: {
		signatureCount: number;
		signatureCountLocalizedCompact: string;
	};
};

type HighchartsPointWithCustomData = typeof Highcharts.Point & {
	custom: ChartDataPoint['custom']; // replace 'any' with the actual type of 'custom'
};

const DEFAULT_MAX_CHANNELS = 4;
export function useBarChart(channelSignatureCounts: ChannelSignatureData[]): UseBarChartReturn {
	const { translate, translatePlural } = useI18n();
	const { colors } = useTheme();

	const track = useTracking();

	const chartData: ChartDataPoint[] = channelSignatureCounts.map((channel) => {
		const color = colors[channel.chartColor];
		return {
			name: translate(channel.translationKey),
			className: `signature-source-bar-${channel.channel}`,
			y: channel.signaturePercent,
			color,
			custom: {
				signatureCount: channel.signatureCount,
				signatureCountLocalizedCompact: channel.signatureCountLocalizedCompact,
			},
		};
	});
	const topChannels = chartData.slice(0, DEFAULT_MAX_CHANNELS);

	const [seriesData, setSeriesData] = useState<ChartDataPoint[]>(topChannels);
	const [isShowingAllChannels, setIsShowingAllChannels] = useState<boolean>(false);
	const barHeight = 65;
	// If there is only one bar, account for padding
	const defaultChartHeight: number = topChannels.length === 1 ? barHeight * 1.3 : topChannels.length * barHeight;
	const [chartHeight, setChartHeight] = useState<number>(defaultChartHeight);

	// Default Options
	const options: Highcharts.Options = useMemo(() => {
		return {
			accessibility: {
				enabled: true,
				keyboardNavigation: { enabled: true },
				announceNewData: { enabled: true },
			},
			lang: {
				accessibility: {
					series: {
						description: translate(
							'corgi.petition.starter_dashboard.petition_insights.signature_sources.chart.description',
						),
					},
					announceNewData: {
						newDataAnnounce: translate(
							'corgi.petition.starter_dashboard.petition_insights.signature_sources.chart.accessibility.new_data_announcement',
						),
					},
					navigator: {
						groupLabel: translate(
							'corgi.petition.starter_dashboard.petition_insights.signature_sources.chart.accessibility.navigator_group_label',
						),
					},
					axis: {
						xAxisDescriptionSingular: translate(
							'corgi.petition.starter_dashboard.petition_insights.signature_sources.chart.accessibility.x_axis_description',
						),
						yAxisDescriptionSingular: translate(
							'corgi.petition.starter_dashboard.petition_insights.signature_sources.chart.accessibility.y_axis_description',
						),
					},
					svgContainerLabel: translate(
						'corgi.petition.starter_dashboard.petition_insights.signature_sources.chart.accessibility.svg_container_label',
					),
					svgContainerTitle: translate(
						'corgi.petition.starter_dashboard.petition_insights.signature_sources.chart.accessibility.svg_container_title',
					),
					defaultChartTitle: translate(
						'corgi.petition.starter_dashboard.petition_insights.signature_sources.chart.accessibility.default_chart_title',
					),
					chartContainerLabel: translate(
						'corgi.petition.starter_dashboard.petition_insights.signature_sources.chart.accessibility.chart_container_label',
					),
					screenReaderSection: {
						beforeRegionLabel: translate(
							'corgi.petition.starter_dashboard.petition_insights.signature_sources.chart.accessibility.before_region_label',
						),
						endOfChartMarker: translate(
							'corgi.petition.starter_dashboard.petition_insights.signature_sources.chart.accessibility.end_of_chart_marker',
						),
					},
					chartTypes: {
						barSingle: translatePlural(
							'corgi.petition.starter_dashboard.petition_insights.signature_sources.chart.accessibility.chart_type_bar_single',
							seriesData.length,
							{ numPoints: seriesData.length },
						),
					},
				},
			},
			chart: {
				height: chartHeight,
				type: 'bar',
				style: {
					fontFamily: '"Noto Sans","Helvetica Neue",Helvetica,Arial,Tahoma,sans-serif',
				},
			},
			title: { text: undefined },
			legend: { enabled: false },
			tooltip: {
				backgroundColor: 'black',
				borderColor: 'white',
				borderWidth: 1,
				style: {
					color: 'white',
				},
				shadow: false,
				formatter() {
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					const customData = (this as unknown as HighchartsPointWithCustomData).custom;
					return translatePlural(
						'corgi.petition.starter_dashboard.petition_insights.signature_sources.chart.tooltip',
						customData.signatureCount,
						{
							channel: this.name,
							signatureCount: customData.signatureCountLocalizedCompact,
						},
					);
				},
			},
			xAxis: {
				title: { text: null },
				gridLineWidth: 0,
				lineWidth: 0,
				type: 'category',
				labels: { style: { fontWeight: 'bold' } },
			},
			yAxis: {
				visible: false,
				min: 0,
				allowDecimals: true,
				title: { text: translate('corgi.petition.starter_dashboard.petition_insights.signature_sources.chart.y_axis') },
				labels: { overflow: 'justify' },
				gridLineWidth: 0,
			},
			series: [
				{
					data: seriesData,
					name: translate('corgi.petition.starter_dashboard.petition_insights.signature_sources.chart.series_name'),
					type: 'bar',
					dataLabels: {
						enabled: true,
						format: '{point.y:.0f}%',
					},
					borderRadius: {
						radius: 8,
						where: 'all',
						scope: 'stack',
					},
					groupPadding: 0,
					pointPadding: 0.1,
					borderWidth: 0,
				},
			],
			credits: { enabled: false },
		};
	}, [chartHeight, seriesData, translate, translatePlural]);

	const showAllChannels = () => {
		setSeriesData(chartData);
		setChartHeight(chartData.length * barHeight);
		setIsShowingAllChannels(true);
		void track('petition_manage_signature_insights_view_more');
	};
	const showTopChannels = () => {
		setSeriesData(topChannels);
		setChartHeight(defaultChartHeight);
		setIsShowingAllChannels(false);
		void track('petition_manage_signature_insights_view_less');
	};
	return {
		data: {
			options,
			isShowingAllChannels,
			showButtons: channelSignatureCounts.length > DEFAULT_MAX_CHANNELS,
			highchartsModule: Highcharts,
		},
		actions: { showAllChannels, showTopChannels },
	};
}
